import { isDateSame, isDateSameOrAfter, isDateSameOrBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { ValidatedNoonReport } from 'src/types/ViewModel/ValidatedNoonReport';
import { isNullOrUndefined } from 'src/utils/objectUtils';
import { ValidatedNoonReportFactory } from '../ValidatedNoonReportFactory';
import { GetSection } from 'src/utils/aolSectionHelper';
import { convertToDate } from 'odfjell-date-time-utils/dateTimeConverter';
import { DateTimeFormat } from 'odfjell-date-time-utils/dateTimeFormatter';

type StoredAolRequest = {
    url?: unknown;
    body?: unknown;
    response?: unknown;
};

export type OfflineAolStoreItem = {
    addNoonReportRequest?: StoredAolRequest;
    saveNoonReportSectionRequests?: StoredAolRequest[];
    updateSectionStatusRequests?: StoredAolRequest[];
    deleteNoonReportRequest?: StoredAolRequest;
    imoNumber?: unknown;
    noonReportGuid?: unknown;
    noonReportId?: unknown;
};

type ReportWindow = {
    from: Date;
    to: Date;
};

type ReportOwnershipIdTargets = {
    metadata?: Record<string, unknown>;
    sectionData: Record<string, unknown>[];
    consumption: Record<string, unknown>[];
    boilerConsumption: Record<string, unknown>[];
};

const STORED_REQUEST_KEYS = [
    'addNoonReportRequest',
    'saveNoonReportSectionRequests',
    'updateSectionStatusRequests',
    'deleteNoonReportRequest',
] as const;

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
    value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;

const hasOwn = (value: object, key: string): boolean => Object.prototype.hasOwnProperty.call(value, key);

const toPositiveSafeInteger = (value: unknown): number | undefined => {
    if (typeof value === 'number') {
        return Number.isSafeInteger(value) && value > 0 ? value : undefined;
    }
    if (typeof value !== 'string' || !/^[0-9]+$/.test(value)) return undefined;

    const numericValue = Number(value);
    return Number.isSafeInteger(numericValue) && numericValue > 0 ? numericValue : undefined;
};

const isValidGuid = (value: unknown): value is string => typeof value === 'string' && GUID_PATTERN.test(value);

const getStoredRequests = (item: OfflineAolStoreItem): StoredAolRequest[] =>
    STORED_REQUEST_KEYS.flatMap((key) => {
        if (!hasOwn(item, key)) return [];
        const value = item[key];
        if (Array.isArray(value)) return value.map((request) => asRecord(request) ?? {});
        return [asRecord(value) ?? {}];
    });

const getUrlSearchParams = (url: unknown): URLSearchParams | undefined => {
    if (typeof url !== 'string' || url.trim() === '') return undefined;

    try {
        return new URL(url, 'https://orcaweb.invalid').searchParams;
    } catch {
        return undefined;
    }
};

const getRequestQueryValues = (request: StoredAolRequest, key: string): string[] => {
    const searchParams = getUrlSearchParams(request.url);
    return searchParams?.getAll(key) ?? [];
};

const getRequestBodyValue = (request: StoredAolRequest, key: string): unknown => {
    const body = asRecord(request.body);
    return body && hasOwn(body, key) ? body[key] : undefined;
};

const getImoSources = (item: OfflineAolStoreItem): { values: number[]; hasInvalidSource: boolean } => {
    const values: number[] = [];
    let hasInvalidSource = !hasOwn(item, 'imoNumber');

    const itemImoNumber = toPositiveSafeInteger(item.imoNumber);
    if (itemImoNumber === undefined) {
        hasInvalidSource = true;
    } else {
        values.push(itemImoNumber);
    }

    getStoredRequests(item).forEach((request) => {
        const queryValues = getRequestQueryValues(request, 'imoNumber');
        if (queryValues.length === 0) {
            hasInvalidSource = true;
        } else {
            queryValues.forEach((value) => {
                const imoNumber = toPositiveSafeInteger(value);
                if (imoNumber === undefined) {
                    hasInvalidSource = true;
                } else {
                    values.push(imoNumber);
                }
            });
        }

        const body = asRecord(request.body);
        if (body && hasOwn(body, 'imoNumber')) {
            const imoNumber = toPositiveSafeInteger(getRequestBodyValue(request, 'imoNumber'));
            if (imoNumber === undefined) {
                hasInvalidSource = true;
            } else {
                values.push(imoNumber);
            }
        }
    });

    return { values, hasInvalidSource };
};

const isOwnVesselItem = (item: OfflineAolStoreItem, activeVesselImoNumber: unknown): boolean => {
    const activeImoNumber = toPositiveSafeInteger(activeVesselImoNumber);
    if (activeImoNumber === undefined) return false;

    const imoSources = getImoSources(item);
    return !imoSources.hasInvalidSource && imoSources.values.length > 0 && imoSources.values.every((imo) => imo === activeImoNumber);
};

const isForeignVesselItem = (item: OfflineAolStoreItem, activeVesselImoNumber: unknown): boolean => {
    const activeImoNumber = toPositiveSafeInteger(activeVesselImoNumber);
    if (activeImoNumber === undefined) return false;

    const imoSources = getImoSources(item);
    if (imoSources.hasInvalidSource || imoSources.values.length === 0) return false;

    const firstImoNumber = imoSources.values[0];
    return imoSources.values.every((imo) => imo === firstImoNumber) && firstImoNumber !== activeImoNumber;
};

export const filterOfflineAolItemsForDisplay = (
    offlineAolItems: OfflineAolStoreItem[],
    activeVesselImoNumber: unknown
): OfflineAolStoreItem[] => offlineAolItems.filter((item) => !isForeignVesselItem(item, activeVesselImoNumber));

const parseDate = (value: unknown): Date | undefined => {
    if (value instanceof Date) return isNaN(value.getTime()) ? undefined : value;
    if (typeof value !== 'string' && typeof value !== 'number') return undefined;

    const date = new Date(value);
    return isNaN(date.getTime()) ? undefined : date;
};

const getItemReportWindow = (item: OfflineAolStoreItem): ReportWindow | undefined => {
    const addRequest = item.addNoonReportRequest;
    const response = asRecord(addRequest?.response);
    const fromResponse = parseDate(response?.reportFromTimeLocalTime);
    const toResponse = parseDate(response?.reportToTimeLocalTime);
    if (fromResponse && toResponse) return { from: fromResponse, to: toResponse };

    const fromUrl = addRequest ? parseDate(getRequestQueryValues(addRequest, 'reportFromTimeLocalTime')[0]) : undefined;
    const toUrl = addRequest ? parseDate(getRequestQueryValues(addRequest, 'reportToTimeLocalTime')[0]) : undefined;
    return fromUrl && toUrl ? { from: fromUrl, to: toUrl } : undefined;
};

const getItemGuidClaims = (item: OfflineAolStoreItem): unknown[] => {
    const claims: unknown[] = [];
    if (hasOwn(item, 'noonReportGuid')) claims.push(item.noonReportGuid);

    getStoredRequests(item).forEach((request) => {
        claims.push(...getRequestQueryValues(request, 'noonReportGuid'));
        const body = asRecord(request.body);
        if (body && hasOwn(body, 'noonReportGuid')) claims.push(getRequestBodyValue(request, 'noonReportGuid'));
    });

    return claims;
};

const getItemReportId = (item: OfflineAolStoreItem): number | undefined => toPositiveSafeInteger(item.noonReportId);

const sameReportWindow = (left: ReportWindow, right: ReportWindow): boolean =>
    isDateSame(left.from, right.from, 'minute') && isDateSame(left.to, right.to, 'minute');

const getReportWindow = (noonReport: unknown): ReportWindow | undefined => {
    const report = asRecord(noonReport);
    const generalSection = asRecord(report?.generalSection);
    const dateTime = asRecord(generalSection?.noonReportDateTime);
    const from = parseDate(dateTime?.reportFromTimeLocalTime);
    const to = parseDate(dateTime?.reportToTimeLocalTime);

    return from && to ? { from, to } : undefined;
};

const getReportMetadata = (noonReport: unknown): Record<string, unknown> | undefined => {
    const report = asRecord(noonReport);
    return asRecord(report?.metadata);
};

const isMissingReportId = (value: unknown): boolean => value === undefined || (typeof value === 'number' && Number.isNaN(value));

const getReportOwnershipIdTargets = (noonReport: unknown): ReportOwnershipIdTargets => {
    const report = asRecord(noonReport);
    const metadata = asRecord(report?.metadata);
    const sectionKeys = [
        'generalSection',
        'activitiesSection',
        'bridgeSection',
        'weatherSection',
        'tankCleaningSection',
        'cargoHeatingSection',
        'enginesSection',
        'freshWaterSection',
        'powerpacksSection',
        'inertGasProductionSection',
        'mrvSection',
        'fuelRobSection',
        'commentsSection',
    ];
    const consumptionSectionKeys = ['enginesSection', 'powerpacksSection', 'inertGasProductionSection'];
    const sections = sectionKeys.map((key) => asRecord(report?.[key])).filter((section): section is Record<string, unknown> => !!section);
    const sectionData = sections
        .map((section) => asRecord(section.sectionData))
        .filter((value): value is Record<string, unknown> => !!value);
    const consumption = consumptionSectionKeys.flatMap((key) => {
        const section = asRecord(report?.[key]);
        const values = section?.consumption;
        if (!Array.isArray(values)) return [];
        return values.map(asRecord).filter((value): value is Record<string, unknown> => !!value);
    });
    const enginesSection = asRecord(report?.enginesSection);
    const boilerConsumption = Array.isArray(enginesSection?.boilerConsumptionV5)
        ? enginesSection.boilerConsumptionV5.map(asRecord).filter((value): value is Record<string, unknown> => !!value)
        : [];

    return { metadata, sectionData, consumption, boilerConsumption };
};

const setReportOwnershipIds = (noonReport: ValidatedNoonReport, noonReportId: number): void => {
    const targets = getReportOwnershipIdTargets(noonReport);
    if (targets.metadata) targets.metadata.noonReportId = noonReportId;
    targets.sectionData.forEach((sectionData) => {
        sectionData.noonReportId = noonReportId;
    });
    targets.consumption.forEach((consumption) => {
        consumption.noonReportId = noonReportId;
    });
    targets.boilerConsumption.forEach((consumption) => {
        consumption.NoonReportId = noonReportId;
    });
};

const isEligibleForIdReconciliation = (
    legacyWinner: ValidatedNoonReport,
    serverReports: ValidatedNoonReport[],
    queueItems: OfflineAolStoreItem[],
    activeVesselImoNumber: unknown
): number | undefined => {
    const activeImoNumber = toPositiveSafeInteger(activeVesselImoNumber);
    const winnerMetadata = getReportMetadata(legacyWinner);
    const winnerId = winnerMetadata?.noonReportId;
    if (activeImoNumber === undefined || !isMissingReportId(winnerId)) return undefined;

    const winnerWindow = getReportWindow(legacyWinner);
    if (!winnerWindow) return undefined;

    const serverRowsInWindow = serverReports.filter((serverReport) => {
        const serverWindow = getReportWindow(serverReport);
        return serverWindow !== undefined && sameReportWindow(serverWindow, winnerWindow);
    });
    if (serverRowsInWindow.length !== 1) return undefined;

    const serverReport = serverRowsInWindow[0];
    const serverMetadata = getReportMetadata(serverReport);
    if (!serverMetadata) return undefined;

    const serverId = toPositiveSafeInteger(serverMetadata.noonReportId);
    const serverGuid = serverMetadata.noonReportGuid;
    const winnerGuid = winnerMetadata?.noonReportGuid;
    const serverImoNumber = hasOwn(serverMetadata, 'imoNumber') ? toPositiveSafeInteger(serverMetadata.imoNumber) : activeImoNumber;

    if (
        serverId === undefined ||
        serverImoNumber !== activeImoNumber ||
        !isValidGuid(serverGuid) ||
        winnerGuid !== serverGuid ||
        !isValidGuid(winnerGuid)
    )
        return undefined;
    if (serverReports.filter((report) => getReportMetadata(report)?.noonReportGuid === serverGuid).length !== 1) return undefined;

    const relevantQueueItems = queueItems.filter((item) => {
        const itemWindow = getItemReportWindow(item);
        const guidClaims = getItemGuidClaims(item);
        const itemReportId = getItemReportId(item);
        return (
            guidClaims.some((claim) => claim === serverGuid) ||
            itemReportId === serverId ||
            (itemWindow !== undefined && sameReportWindow(itemWindow, winnerWindow))
        );
    });

    if (relevantQueueItems.length !== 1) return undefined;

    const [queueItem] = relevantQueueItems;
    if (queueItem.deleteNoonReportRequest || !isOwnVesselItem(queueItem, activeVesselImoNumber)) return undefined;

    const guidClaims = getItemGuidClaims(queueItem);
    if (guidClaims.length === 0 || guidClaims.some((claim) => !isValidGuid(claim) || claim !== serverGuid)) return undefined;

    const queueWindow = getItemReportWindow(queueItem);
    if (queueWindow && !sameReportWindow(queueWindow, winnerWindow)) return undefined;

    const queueReportId = getItemReportId(queueItem);
    if (queueReportId !== undefined && queueReportId !== serverId) return undefined;

    return serverId;
};

export const reconcileEligibleNoonReportIds = (
    legacyReports: ValidatedNoonReport[],
    originalServerReports: ValidatedNoonReport[],
    queueItems: OfflineAolStoreItem[],
    activeVesselImoNumber: unknown
): void => {
    legacyReports.forEach((legacyReport) => {
        const serverId = isEligibleForIdReconciliation(legacyReport, originalServerReports, queueItems, activeVesselImoNumber);
        if (serverId !== undefined) setReportOwnershipIds(legacyReport, serverId);
    });
};

export const mapOfflineAolItemsForNewReports = (offlineAolItems: any[], fromDate: Date, toDate: Date) => {
    const validatedNoonReportFactory = new ValidatedNoonReportFactory();

    return offlineAolItems
        .filter((a) => !!a.addNoonReportRequest)
        .map((offlineAolItem) => {
            const noonReport = validatedNoonReportFactory.createNewOfflineReport(structuredClone(offlineAolItem.addNoonReportRequest.response));

            offlineAolItem.saveNoonReportSectionRequests?.forEach((saveNoonReportSectionRequest) => {
                validatedNoonReportFactory.updateOfflineReport(noonReport, structuredClone(saveNoonReportSectionRequest.response));
            });
            updateSectionStatuses(offlineAolItem, noonReport);

            return noonReport;
        })
        .filter((n) => {
            const noonReportDate = convertToDate(n.generalSection.noonReportDateTime.aolDateLocalTime, DateTimeFormat.queryFormats.dateOnly);
            return isDateSameOrAfter(noonReportDate, fromDate, 'day') && isDateSameOrBefore(noonReportDate, toDate, 'day');
        });
};

export const mapOfflineNoonReports = (
    offlineAolItems: any[],
    offlineNoonReports: ValidatedNoonReport[],
    noonReports: ValidatedNoonReport[],
    fromDate: Date,
    toDate: Date
) => {
    const offlineAolItemsForExistingReports = offlineAolItems.filter((a) => !a.addNoonReportRequest);
    const validatedNoonReportFactory = new ValidatedNoonReportFactory();
    const deletedNoonReports = offlineAolItemsForExistingReports.filter((n) => n.deleteNoonReportRequest);

    noonReports = noonReports.filter((n) =>
        !deletedNoonReports.some((d) => 
            (d.noonReportGuid && n.metadata.noonReportGuid === d.noonReportGuid) ||
            (!d.noonReportGuid && n.metadata.noonReportId === +d.noonReportId)
        )
    );

    offlineNoonReports.forEach((offlineNoonReport) => {
        const indexOfReport = findIndexOfNoonReportByTime(
            noonReports,
            offlineNoonReport.generalSection.noonReportDateTime.reportFromTimeLocalTime,
            offlineNoonReport.generalSection.noonReportDateTime.reportToTimeLocalTime
        );

        if (indexOfReport > -1) {
            noonReports[indexOfReport] = offlineNoonReport;
        } else {
            noonReports.push(offlineNoonReport);
        }
    });

    noonReports = noonReports.filter((n) => !isNullOrUndefined(n));
    noonReports.forEach((noonReport) => {
        const offlineAolItem = offlineAolItemsForExistingReports.find((p) => 
            (p.noonReportGuid && p.noonReportGuid === noonReport.metadata.noonReportGuid) ||
            (!p.noonReportGuid && p.noonReportId === noonReport.metadata.noonReportId)
        );
        if (offlineAolItem?.saveNoonReportSectionRequests) {
            offlineAolItem.saveNoonReportSectionRequests.forEach((saveNoonReportSectionRequest) => {
                validatedNoonReportFactory.updateOfflineReport(noonReport, structuredClone(saveNoonReportSectionRequest.response));
            });

            updateSectionStatuses(offlineAolItem, noonReport);
        }
    });

    return noonReports.filter((n) => {
        const noonReportDate = convertToDate(n.generalSection.noonReportDateTime.aolDateLocalTime, DateTimeFormat.queryFormats.dateOnly);
        return isDateSameOrAfter(noonReportDate, fromDate, 'day') && isDateSameOrBefore(noonReportDate, toDate, 'day');
    });
};

export const updateSectionStatuses = (aolStoreItem, noonReport: ValidatedNoonReport) => {
    if (aolStoreItem.updateSectionStatusRequests) {
        aolStoreItem.updateSectionStatusRequests.forEach((updateSectionStatusRequest) => {
            const section = GetSection(+updateSectionStatusRequest.response.noonReportSectionEnum, noonReport);

            if (section?.sectionData) {
                section.sectionData.isApproved = updateSectionStatusRequest.response.isSectionApproved;
                section.sectionData.isCompleted = updateSectionStatusRequest.response.isSectionCompleted;
            }
        });
    }
};

const findIndexOfNoonReportByTime = (noonReports: ValidatedNoonReport[], startTime: Date, stopTime: Date) =>
    noonReports.findIndex(
        (n) =>
            isDateSame(n.generalSection.noonReportDateTime.reportFromTimeLocalTime, startTime, 'minute') &&
            isDateSame(n.generalSection.noonReportDateTime.reportToTimeLocalTime, stopTime, 'minute')
    );
